import React, { useEffect, useRef, useState } from "react";

import type { LandingPageContent } from "../content/types";

const footerLogoSrc = "/assets/brand/production/v1/svg/erg-dbk-logo-horizontal-on-dark.svg";
const consentStorageKey = "dynamicbooks.cmp.v1";

type ConsentChoice = "necessary" | "analytics";

type StoredConsent = {
  version: 1;
  choice: ConsentChoice;
  savedAt: string;
};

type SiteFooterProps = {
  content: LandingPageContent["footer"];
  logoSrc?: string;
};

export function SiteFooter({ content, logoSrc = footerLogoSrc }: SiteFooterProps) {
  const [choice, setChoice] = useState<ConsentChoice | null>(null);
  const [draftChoice, setDraftChoice] = useState<ConsentChoice>("necessary");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const settingsRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const showBanner = loaded && choice === null && !settingsOpen;

  useEffect(() => {
    const stored = readStoredConsent();
    setChoice(stored);
    setDraftChoice(stored ?? "necessary");
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!settingsOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeSettings();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    settingsRef.current?.focus();

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [settingsOpen]);

  function openSettings(opener?: HTMLElement | null) {
    returnFocusRef.current = opener ?? (document.activeElement as HTMLElement | null);
    setDraftChoice(choice ?? "necessary");
    setSettingsOpen(true);
  }

  function closeSettings() {
    setSettingsOpen(false);
    window.setTimeout(() => {
      const opener = returnFocusRef.current;
      if (opener?.isConnected) {
        opener.focus();
      } else {
        document.querySelector<HTMLElement>('[data-cmp-settings-trigger="banner"]')?.focus();
      }
      returnFocusRef.current = null;
    }, 0);
  }

  function saveChoice(nextChoice: ConsentChoice) {
    const stored: StoredConsent = {
      version: 1,
      choice: nextChoice,
      savedAt: new Date().toISOString()
    };
    try {
      window.localStorage.setItem(consentStorageKey, JSON.stringify(stored));
    } catch (_) {
      // Consent remains in memory if local storage is unavailable.
    }
    setChoice(nextChoice);
    setDraftChoice(nextChoice);
    setSettingsOpen(false);
    dispatchConsent(nextChoice);
  }

  return (
    <>
      <footer className="site-footer" id={content.id}>
        <div className="db-container site-footer__inner">
          <div className="site-footer__brand">
            <img className="site-footer__logo" src={logoSrc} width="180" height="60" alt="" loading="lazy" decoding="async" />
            <p className="site-footer__owner">{content.owner}</p>
            <p>{content.summary}</p>
            <p className="site-footer__copyright">{content.copyright}</p>
          </div>
          <nav className="site-footer__links" aria-label="Footer navigation">
            {content.groups.map((group) => (
              <div className="site-footer__group" key={group.id}>
                <h2>{group.title}</h2>
                <ul>
                  {group.links.map((link) => (
                    <li key={link.id}>
                      {link.action === "cookie-settings" ? (
                        <button
                          className="site-footer__link-button"
                          type="button"
                          onClick={(event) => openSettings(event.currentTarget)}
                        >
                          {link.label}
                        </button>
                      ) : (
                        <a href={link.href}>{link.label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>
      </footer>

      {showBanner ? (
        <section className="cmp-banner" role="region" aria-labelledby="cmp-banner-title">
          <div>
            <h2 id="cmp-banner-title">{content.cmp.bannerTitle}</h2>
            <p>{content.cmp.bannerBody}</p>
          </div>
          <div className="cmp-actions">
            <button className="db-button db-button--secondary" type="button" onClick={() => saveChoice("necessary")}>
              {content.cmp.necessaryOnlyLabel}
            </button>
            <button className="db-button db-button--secondary" type="button" data-cmp-settings-trigger="banner" onClick={(event) => openSettings(event.currentTarget)}>
              {content.cmp.settingsLabel}
            </button>
            <button className="db-button db-button--primary" type="button" onClick={() => saveChoice("analytics")}>
              {content.cmp.acceptAllLabel}
            </button>
          </div>
        </section>
      ) : null}

      {settingsOpen ? (
        <div className="cmp-settings-backdrop" role="presentation">
          <section
            className="cmp-settings"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cmp-settings-title"
            tabIndex={-1}
            ref={settingsRef}
          >
            <header>
              <div>
                <p className="section-eyebrow">{content.cmp.currentChoiceLabel}</p>
                <h2 id="cmp-settings-title">{content.cmp.settingsTitle}</h2>
              </div>
              <button className="cmp-icon-button" type="button" aria-label={content.cmp.closeLabel} onClick={closeSettings}>
                ×
              </button>
            </header>

            <div className="cmp-choice-list">
              <label className="cmp-choice">
                <input type="radio" name="dynamicbooks-cmp-choice" checked={draftChoice === "necessary"} onChange={() => setDraftChoice("necessary")} />
                <span>
                  <strong>{content.cmp.necessaryOnlyLabel}</strong>
                  <small>{content.cmp.necessaryCategory}</small>
                </span>
              </label>
              <label className="cmp-choice">
                <input type="radio" name="dynamicbooks-cmp-choice" checked={draftChoice === "analytics"} onChange={() => setDraftChoice("analytics")} />
                <span>
                  <strong>{content.cmp.analyticsToggleLabel}</strong>
                  <small>{content.cmp.analyticsCategory}</small>
                </span>
              </label>
            </div>

            <p className="cmp-current-choice" aria-live="polite">
              {draftChoice === "analytics" ? content.cmp.analyticsSaved : content.cmp.necessarySaved}
            </p>

            <footer className="cmp-settings__actions">
              <button className="db-button db-button--secondary" type="button" onClick={closeSettings}>
                {content.cmp.closeLabel}
              </button>
              <button className="db-button db-button--primary" type="button" onClick={() => saveChoice(draftChoice)}>
                {content.cmp.saveChoiceLabel}
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  );
}

function readStoredConsent(): ConsentChoice | null {
  try {
    const raw = window.localStorage.getItem(consentStorageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (parsed.version === 1 && (parsed.choice === "necessary" || parsed.choice === "analytics")) {
      return parsed.choice;
    }
  } catch (_) {
    return null;
  }

  return null;
}

function dispatchConsent(choice: ConsentChoice) {
  try {
    window.dispatchEvent(new CustomEvent("dynamicbooks:cmp-consent", {
      detail: {
        analytics: choice === "analytics",
        choice
      }
    }));
  } catch (_) {}
}
